import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Table, TableCell, TableHead, TableHeader, TableRow, TableWrap } from '@/components/ui/Table';
import { EditableSuggestedBidCell } from '@/components/recommendations/EditableSuggestedBidCell';
import { RecommendationBulkBar } from '@/components/recommendations/RecommendationBulkBar';
import { RecommendationReasonTooltip } from '@/components/recommendations/RecommendationReasonTooltip';
import { RecommendationTableToolbar } from '@/components/recommendations/RecommendationTableToolbar';
import {
  adGroupLabel,
  campaignDetailPath,
  formatRecommendationCurrency,
  searchTermRowKey,
} from '@/api/recommendationFormat';
import { searchTermRecommendationFingerprint } from '@/api/recommendationDismiss';
import {
  actionTypeLabel,
  filterByAction,
  filterBySearch,
  isAsinLikeTerm,
  sortByMetric,
  stripDuplicateRoasFromReason,
  type RecommendationSortField,
  type RecommendationSortOrder,
} from '@/api/recommendationUtils';
import type {
  KeywordRecommendationAction,
  SearchTermRecommendationItem,
  SpKeywordApplyAction,
} from '@/types/keywordRecommendations';
import { useMemo } from 'react';

type RecommendationTableVariant = 'profile' | 'campaign-scoped';

type SearchTermRecommendationsTableProps = {
  profileId: string;
  rows: SearchTermRecommendationItem[];
  selected: Set<string>;
  bidOverrides: Record<string, number>;
  applying: boolean;
  searchQuery: string;
  actionFilter: KeywordRecommendationAction | 'ALL';
  sortBy: RecommendationSortField;
  sortOrder: RecommendationSortOrder;
  onSearchChange: (value: string) => void;
  onActionFilterChange: (value: KeywordRecommendationAction | 'ALL') => void;
  onSortByChange: (value: RecommendationSortField) => void;
  onSortOrderChange: (value: RecommendationSortOrder) => void;
  onToggleSelect: (key: string) => void;
  onSelectAll: (keys: string[]) => void;
  onBidChange: (key: string, bid: number) => void;
  onApply: (action: SpKeywordApplyAction) => void;
  onDismiss: (fingerprints: string[]) => void;
  onBulkApply: () => void;
  onBulkDismiss: () => void;
  onClearSelection: () => void;
  buildApplyAction: (
    row: SearchTermRecommendationItem,
    bidOverrides: Record<string, number>
  ) => SpKeywordApplyAction | null;
  variant?: RecommendationTableVariant;
  adGroupNameById?: Map<string, string>;
  applyDisabled?: boolean;
  applyDisabledReason?: string;
};

const DEFAULT_APPLY_DISABLED_REASON =
  'Apply is available for Sponsored Products keywords only.';

export function SearchTermRecommendationsTable({
  profileId,
  rows,
  selected,
  bidOverrides,
  applying,
  searchQuery,
  actionFilter,
  sortBy,
  sortOrder,
  onSearchChange,
  onActionFilterChange,
  onSortByChange,
  onSortOrderChange,
  onToggleSelect,
  onSelectAll,
  onBidChange,
  onApply,
  onDismiss,
  onBulkApply,
  onBulkDismiss,
  onClearSelection,
  buildApplyAction,
  variant = 'profile',
  adGroupNameById,
  applyDisabled = false,
  applyDisabledReason = DEFAULT_APPLY_DISABLED_REASON,
}: SearchTermRecommendationsTableProps) {
  const isCampaignScoped = variant === 'campaign-scoped';

  const filteredRows = useMemo(() => {
    const searched = filterBySearch(rows, searchQuery, (row) => {
      const adGroupName = adGroupLabel(row.adGroupId, adGroupNameById);
      if (isCampaignScoped) {
        return [row.searchTerm, adGroupName, row.adGroupId];
      }
      return [row.searchTerm, row.campaignName || '', row.campaignId, adGroupName, row.adGroupId];
    });
    const actionFiltered = filterByAction(searched, actionFilter, (row) => row.recommendation.action);
    return sortByMetric(actionFiltered, sortBy, sortOrder, (row) => row.metrics);
  }, [rows, searchQuery, actionFilter, sortBy, sortOrder, isCampaignScoped, adGroupNameById]);

  const selectableKeys = useMemo(
    () =>
      filteredRows
        .map((row) => searchTermRowKey(row.campaignId, row.adGroupId, row.searchTerm))
        .filter((_, index) => buildApplyAction(filteredRows[index], bidOverrides) != null),
    [filteredRows, bidOverrides, buildApplyAction]
  );

  const allSelected =
    selectableKeys.length > 0 && selectableKeys.every((key) => selected.has(key));
  const someSelected = selectableKeys.some((key) => selected.has(key));

  if (!rows.length) {
    return (
      <section className="overflow-hidden rounded-xl border border-border bg-surface">
        <div className="border-b border-border bg-page px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">Search term suggestions</h3>
          <p className="mt-0.5 text-xs text-foreground-muted">
            Converting search terms not yet targeted as keywords.
          </p>
        </div>
        <p className="px-4 py-6 text-sm text-foreground-subtle">No search term suggestions for this range.</p>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-surface">
      <div className="border-b border-border bg-page px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">Search term suggestions</h3>
        <p className="mt-0.5 text-xs text-foreground-muted">
          Converting search terms not yet targeted as keywords.
        </p>
      </div>

      <RecommendationTableToolbar
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        actionFilter={actionFilter}
        onActionFilterChange={onActionFilterChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortByChange={onSortByChange}
        onSortOrderChange={onSortOrderChange}
        showingCount={filteredRows.length}
        totalCount={rows.length}
        searchPlaceholder={
          isCampaignScoped ? 'Search term or ad group…' : 'Search term, campaign, or ad group…'
        }
        showActionFilter
      />

      <div className="px-3 pt-2">
        <RecommendationBulkBar
          selectedCount={selected.size}
          onApplySelected={onBulkApply}
          onDismissSelected={onBulkDismiss}
          onClearSelection={onClearSelection}
          applying={applying}
          applyDisabled={applyDisabled}
          applyDisabledReason={applyDisabledReason}
        />
      </div>

      <TableWrap className="max-h-[60vh] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow className="divide-y-0 hover:bg-transparent">
              <TableHead scope="col" className="w-10 px-3 py-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={() => onSelectAll(allSelected ? [] : selectableKeys)}
                  aria-label="Select all search term suggestions"
                />
              </TableHead>
              <TableHead scope="col" className="min-w-[10rem] px-3 py-2">
                Search term
              </TableHead>
              {!isCampaignScoped ? (
                <TableHead scope="col" className="min-w-[9rem] px-3 py-2">
                  Campaign
                </TableHead>
              ) : null}
              <TableHead scope="col" className="min-w-[7rem] px-3 py-2">
                Ad group
              </TableHead>
              <TableHead scope="col" className="px-3 py-2">
                Match
              </TableHead>
              <TableHead scope="col" className="px-3 py-2 text-right">
                Clicks
              </TableHead>
              <TableHead scope="col" className="px-3 py-2 text-right">
                Spend
              </TableHead>
              <TableHead scope="col" className="px-3 py-2 text-right">
                Sales
              </TableHead>
              <TableHead scope="col" className="px-3 py-2 text-right">
                ROAS
              </TableHead>
              <TableHead scope="col" className="px-3 py-2 text-right">
                Suggested bid
              </TableHead>
              <TableHead scope="col" className="px-3 py-2">
                Type
              </TableHead>
              <TableHead scope="col" className="w-32 px-3 py-2">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <tbody className="divide-y divide-border">
            {filteredRows.map((row) => {
              const key = searchTermRowKey(row.campaignId, row.adGroupId, row.searchTerm);
              const applyAction = buildApplyAction(row, bidOverrides);
              const suggestedBid =
                bidOverrides[key] ?? row.recommendation.suggestedBid ?? 1.0;
              const reason = stripDuplicateRoasFromReason(
                row.recommendation.reason,
                row.metrics.roas
              );
              const fingerprint = searchTermRecommendationFingerprint(
                row.campaignId,
                row.adGroupId,
                row.searchTerm
              );

              return (
                <TableRow key={key}>
                  <TableCell compact className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(key)}
                      onChange={() => onToggleSelect(key)}
                      disabled={!applyAction}
                      aria-label={`Select ${row.searchTerm}`}
                    />
                  </TableCell>
                  <TableCell compact className="px-3 py-2">
                    <div className="font-medium text-foreground">{row.searchTerm}</div>
                    {isAsinLikeTerm(row.searchTerm) ? (
                      <span className="mt-1 inline-flex rounded bg-warning-subtle px-1.5 py-0.5 text-[0.65rem] font-medium text-warning-ink">
                        Possible ASIN
                      </span>
                    ) : null}
                  </TableCell>
                  {!isCampaignScoped ? (
                    <TableCell compact className="px-3 py-2">
                      <Link
                        to={campaignDetailPath(profileId, row.campaignId)}
                        className="line-clamp-2 text-brand hover:underline"
                        title={row.campaignName || row.campaignId}
                      >
                        {row.campaignName || row.campaignId}
                      </Link>
                    </TableCell>
                  ) : null}
                  <TableCell compact className="px-3 py-2 text-foreground-muted">
                    {adGroupLabel(row.adGroupId, adGroupNameById)}
                  </TableCell>
                  <TableCell compact className="px-3 py-2 text-foreground-muted">
                    {row.recommendation.matchType || '—'}
                  </TableCell>
                  <TableCell compact className="px-3 py-2 text-right font-mono tabular-nums">
                    {row.metrics.clicks}
                  </TableCell>
                  <TableCell compact className="px-3 py-2 text-right font-mono tabular-nums">
                    {formatRecommendationCurrency(row.metrics.cost)}
                  </TableCell>
                  <TableCell compact className="px-3 py-2 text-right font-mono tabular-nums">
                    {formatRecommendationCurrency(row.metrics.sales30d)}
                  </TableCell>
                  <TableCell compact className="px-3 py-2 text-right font-mono tabular-nums">
                    {row.metrics.roas.toFixed(2)}
                  </TableCell>
                  <TableCell compact className="px-3 py-2 text-right">
                    {row.recommendation.action === 'ADD_KEYWORD' ? (
                      <EditableSuggestedBidCell
                        value={suggestedBid}
                        onChange={(bid) => onBidChange(key, bid)}
                      />
                    ) : (
                      <span className="text-chrome-label">—</span>
                    )}
                  </TableCell>
                  <TableCell compact className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <span className="inline-flex rounded bg-surface-muted px-2 py-0.5 text-xs text-foreground-muted">
                        {actionTypeLabel(row.recommendation.action)}
                      </span>
                      <RecommendationReasonTooltip reason={reason} />
                    </div>
                  </TableCell>
                  <TableCell compact className="px-3 py-2">
                    <div className="flex flex-col gap-1">
                      {applyAction ? (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => onApply(applyAction)}
                          disabled={applying || applyDisabled}
                          title={applyDisabled ? applyDisabledReason : undefined}
                        >
                          Apply
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onDismiss([fingerprint])}
                        disabled={applying}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>

      {filteredRows.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-foreground-subtle">
          No rows match your filters.
        </p>
      ) : null}
    </section>
  );
}
